import React, { useContext, useEffect, useState } from "react";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import { useLocation } from "react-router-dom";
import IOrder from "../../../models/IOrder";
import IBreadcrumbs from "../../../models/IBreadcrumbs";
import IVendorModel from "../../../models/IVendorModel";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IGetOrderDTO from "../../../models/DTO/IGetOrderDTO";
import IOnGetOrderDTO from "../../../models/DTO/IOnGetOrderDTO";
import * as _ from "lodash-es";
import IOrder_Item from "../../../models/IOrder_Item";
import { FormattedMessage } from "react-intl";
import { NumericFormat } from "react-number-format";
import Breadcrumbs from "../../shared/Breadcrumbs/Breadcrumbs";
import OwPrintButton from "../../shared/OwPrintButton/OwPrintButton";
import { OrderTotalsPanel } from "../../order_entry/OrderTotalsPanel/OrderTotalsPanel";
import { QuotePrintDetailView } from "./QuotePrintDetailView/QuotePrintDetailView";
import { useParams } from "react-router-dom";
import { SocketContext } from "../../../contexts/SocketContext";
import UiContext from "../../../contexts/UiContext";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import { OwViewOptionsPanel } from "../../shared/OwViewOptionsPanel/OwViewOptionsPanel";
import { useQuoteViewOptions } from "../../../hooks/quote/useQuoteViewOptions";
import { useQuoteViewOptionsUI } from "../../../hooks/quote/useQuoteViewOptionsUI";
import SupplierService from "../../../services/SupplierService";
import IUpdateSupplierSettingsDTO from "../../../models/DTO/IUpdateSupplierSettingsDTO";
import IOnUpdateSupplierSettingsDTO from "../../../models/DTO/IOnUpdateSupplierSettingsDTO";
import INotification from "../../../models/INotification";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import "./QuotePrintView.scss";

export const QuotePrintView: React.FC = () => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const location = useLocation();
    const params: any = useParams();

    const supplierSettings = SupplierService.getSupplierSettings(supplierModel);

    const [viewOptions, handleViewOptionChange] = useQuoteViewOptions(supplierSettings);
    const optionsPanelViewOptions = useQuoteViewOptionsUI(viewOptions, supplierModel);

    const [order, setOrder] = useState<IOrder>();
    const [orderGuid, setOrderGuid] = useState<string>();
    const [navItems, setNavItems] = useState<IBreadcrumbs[]>([]);
    const [vendorModel, setVendorModel] = useState<IVendorModel>();

    const onGetOrder = (data: IOnGetOrderDTO) => {
        setOrder(data.order);

        setNavItems([
            { title: "Dashboard", url: "/dashboard" },
            { title: "Quotes", url: "/quotes" },
            {
                title: `Quote ${data.order.name}`,
                skipTranslate: true,
                url: `/quotes/${data.order.guid}/entry`,
            },
            {
                title: "Print View",
                url: "",
            },
        ]);
    };

    const onUpdateSupplierSettings = (data: IOnUpdateSupplierSettingsDTO) => {
        const message: INotification = {
            position: NotificationPositionEnum.bc,
            level: NotificationLevelEnum.success,
            message: "successfully_updated_settings",
        };

        uiContext.notifyFn(null, message);
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.OrderOnGetOrder, fn: onGetOrder },
        { endpoint: EndpointsEnum.SupplierOnUpdateSupplierSettings, fn: onUpdateSupplierSettings },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        if (!orderGuid && params.guid) {
            const _orderGuid = params.guid;

            if (_orderGuid) {
                setOrderGuid(_orderGuid);
            }
        }

        return () => {
            for (const _socket of sockets) {
                socket.off(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    useEffect(() => {
        getOrder();
    }, [orderGuid]);

    const getOrder = () => {
        if (!orderGuid) {
            return;
        }

        const payload: IGetOrderDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            orderGuid,
        };

        socket.emit(EndpointsEnum.OrderGetOrder, payload);
    };

    const saveViewOptions = () => {
        const payload: IUpdateSupplierSettingsDTO = {
            supplierGuid: supplierModel.guid,
            userGuid: userModel.guid,
            userToken: userModel.token,
            // @ts-expect-error
            settings: {
                quote: {
                    show_phone: viewOptions.showPhone,
                    show_email: viewOptions.showEmail,
                    show_order_qty: viewOptions.showOrderQty,
                    show_order_desc: viewOptions.showOrderDescription,
                    show_sku: viewOptions.showSku,
                    show_upc: viewOptions.showUpc,
                    hide_sku_col: viewOptions.hideSkuCol,
                    show_bill_to: viewOptions.showBillTo,
                    show_bill_to_phone: viewOptions.showBillToPhone,
                    show_ship_to_phone: viewOptions.showShipToPhone,
                    show_order_user: viewOptions.showOrderUser,
                    show_uom: viewOptions.showUom,
                    show_item_photo: viewOptions.showItemPhoto,
                    hide_business_name: viewOptions.hideBusinessName,
                    show_dimensions: viewOptions.showDimensions,
                    inline_logo_name: viewOptions.inlineLogoName,
                },
            },
        };

        socket.emit(EndpointsEnum.SupplierUpdateSupplierSettings, payload);
    };

    const getTotals = () => {
        if (!order) {
            return null;
        }

        let subTotal = 0;

        _.each(order.Order_Items, (orderItem: IOrder_Item) => {
            const rowTotal = Number(orderItem.cost_price_frozen) * Number(orderItem.qty);

            subTotal += rowTotal;
        });

        let tax_rate = Number(supplierModel.tax_rate);

        if (tax_rate > 1) {
            tax_rate = tax_rate / 100;
        }

        const tax = subTotal * tax_rate;
        const grand_total = subTotal + tax;

        return (
            <div className="col-12 col-md-3 offset-md-9 text-end pe-4">
                <div className="row">
                    <span className="col-6">
                        <FormattedMessage id="sub_total" defaultMessage="Sub Total" description="Sub Total Title" />
                    </span>
                    <span className="col-6">
                        <NumericFormat
                            value={subTotal}
                            displayType={"text"}
                            thousandSeparator={true}
                            decimalScale={2}
                            fixedDecimalScale={true}
                            prefix={"$"}
                        />
                    </span>
                </div>

                <div className="row ">
                    <span className="col-6">
                        <FormattedMessage id="tax" defaultMessage="Tax" description="Tax Title" />
                        <span className={"ms-1"}>({supplierModel.tax_rate}%)</span>
                    </span>
                    <span className="col-6">
                        <NumericFormat
                            value={tax}
                            displayType={"text"}
                            thousandSeparator={true}
                            decimalScale={2}
                            fixedDecimalScale={true}
                            prefix={"$"}
                        />
                    </span>
                </div>

                <div className="row">
                    <hr className={""} />
                    <span className="col-6">
                        <FormattedMessage id="grand_total" defaultMessage="Grand Total" description="Grand Total Title" />
                    </span>
                    <span className="col-6">
                        <NumericFormat
                            value={grand_total}
                            displayType={"text"}
                            thousandSeparator={true}
                            decimalScale={2}
                            fixedDecimalScale={true}
                            prefix={"$"}
                        />
                    </span>
                </div>
            </div>
        );
    };

    return (
        <div className="QuotePrintView col-12 mt-2 pb-5">
            <OwViewOptionsPanel
                saveFn={saveViewOptions}
                showSaveBtn={true}
                viewOptions={optionsPanelViewOptions}
                onChangeFn={handleViewOptionChange}
            />

            <div className={"row mb-2 d-flex justify-content-between align-items-center flex-wrap d-print-none"}>
                <div className={"d-inline-flex"}>
                    <Breadcrumbs hasTopMargin={false} navItems={navItems} />
                </div>
                <div className={"text-end"}>
                    <OwPrintButton />
                </div>
            </div>

            {order ? <QuotePrintDetailView order={order} viewOptions={viewOptions} /> : null}

            {order ? (
                <div className={"row"}>
                    <div className={"col-12 justify-content-end d-flex"}>
                        <OrderTotalsPanel
                            shouldHidePricingFn={() => {
                                return false;
                            }}
                            shouldHideTaxJarName={true}
                            order={order}
                            orderItemType={"standard"}
                            isReadOnly={true}
                            lineView={true}
                        />
                    </div>
                </div>
            ) : null}

            {supplierModel.sales_order_footer ? <div className={"text-center"}>{supplierModel.sales_order_footer}</div> : null}
        </div>
    );
};
